<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Carousel, Slide } from 'vue3-carousel';
import DeveloperCard from '@/components/partials/DeveloperCard.vue';

// Define props and destructure them
const props = defineProps({
  title: String,
  testimonials: Array,
});

const carouselModel = ref(0);
const isMobile = ref(window.innerWidth < 1300);

// Update isMobile on window resize
const updateIsMobile = () => {
  isMobile.value = window.innerWidth < 1300;
};

onMounted(() => {
  window.addEventListener('resize', updateIsMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
});

// Compute items to show based on isMobile and number of testimonials
const itemsToShow = computed(() => {
  // If there's only one testimonial, show 1 card regardless of screen size
  if (props.testimonials.length === 1) {
    return 1;
  }

  // Use isMobile to determine the number of cards to show
  return isMobile.value ? 1 : 2; // 1 card on mobile/tablet, 2 cards on desktop
});

// Determine if navigation should be visible (hidden if only 1 testimonial)
const showNavigation = computed(() => props.testimonials.length > 1);
</script>

<template>
  <div class="talent-testimonial-section-wrapper">
    <div class="talent-testimonial-section" id="testimonial">
      <h2 class="section-title">{{ title }}<span class="title-color">Testimonial</span></h2>
      <!-- Carousel for all screen sizes -->
      <div class="testimonials-carousel">
        <Carousel
          v-model="carouselModel"
          :wrap-around="true"
          :items-to-show="itemsToShow"
          :items-to-scroll="1"
          :snap-align="'start'"
        >
          <Slide
            v-for="(testimonial, index) in testimonials"
            :key="index"
            class="carousel-slide"
          >
            <div class="card-wrapper">
              <DeveloperCard
                :name="testimonial.name"
                :title="testimonial.title"
                :message="testimonial.message"
                :has-image="testimonial.hasImage"
              />
            </div>
          </Slide>
        </Carousel>
        <!-- Navigation and Slider Indicators (only shown if more than 1 testimonial) -->
        <div v-if="showNavigation" class="navigation-wrapper">
          <v-icon
            class="carousel-arrow prev-arrow"
            @click="carouselModel = carouselModel > 0 ? carouselModel - 1 : testimonials.length - 1"
          >
            mdi-chevron-left
          </v-icon>
          <div class="slider-indicators">
            <div
              v-for="(_, index) in testimonials"
              :key="index"
              class="indicator"
              :class="{ 'active': index === carouselModel, 'current': index === carouselModel }"
              @click="carouselModel = index"
            ></div>
          </div>
          <v-icon
            class="carousel-arrow next-arrow"
            @click="carouselModel = carouselModel < testimonials.length - 1 ? carouselModel + 1 : 0"
          >
            mdi-chevron-right
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.talent-testimonial-section-wrapper {
  width: 100%;
  padding-top: 2rem;
}

.talent-testimonial-section {
  max-width: 1200px;
  margin: 0 auto;
  text-align: left;
}

.section-title {
  font-size: 42px;
  font-weight: bold;
  margin-bottom: 20px;
}

.title-color {
  color: #ff6860; /* Red color from the design */
}

.testimonials-carousel {
  position: relative;
}

.carousel-slide {
  display: flex;
  justify-content: left;
  padding: 0 10px;
}

.card-wrapper {
  width: 100%;
  max-width: 500px; /* Fixed width for all cards */
  min-height: 350px; /* Fixed height for all cards */
  display: flex;
  flex-direction: column;
}

/* Navigation and Indicators */
.navigation-wrapper {
  margin-left: 2rem;
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 20px;
  margin-top: 1rem;
  padding-bottom: 1rem;
}

.carousel-arrow {
  font-size: 24px;
  color: #2196f3; /* Blue color to match the design */
  cursor: pointer;
  transition: color 0.3s ease;
}

.carousel-arrow:hover {
  color: #1e90ff; /* Slightly darker blue on hover */
}

.slider-indicators {
  display: flex;
  justify-content: left;
  align-items: left;
  gap: 8px;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0e0e0;
  transition: all 0.3s ease;
  cursor: pointer;
}

.indicator.active {
  background: #2196f3; /* Blue color for active indicator */
}

.indicator.current {
  width: 24px;
  border-radius: 4px;
}

/* Mobile: Stacked Grid */
.testimonials-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  padding: 0 15px;
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .testimonials-carousel {
    :deep(.carousel) {
      --vc-ih: 1 !important; /* Show only 1 item on tablet */
    }
  }

  .card-wrapper {
    max-width: 100%; /* Allow card to take full width on tablet */
  }
}

@media (max-width: 768px) {
  .section-title {
    font-size: 32px;
    padding-left: 0rem;
  }

  .carousel-slide {
    padding: 0 5px;
  }

  .card-wrapper {
    max-width: 100%;
    min-height: 300px; /* Slightly smaller height on mobile */
  }
}
</style>